import React, { useEffect, useState } from "react";
import { Box, IconButton, Typography, Button, TextField, MenuItem, Select, Checkbox, FormControl } from "@mui/material";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useAssetsContext } from "../../context/AssetsContext";
import { ImageFilter, AssetFilters, defaultFilters, hasActiveAssetFilters } from "./config";
import { useSession } from "../../context/SessionContext";

const TextFieldComponent = ({
  label,
  value,
  type,
  onChange,
  "data-cy": dataCy
}: {
  label: string;
  value: string | number | undefined | true | any;
  type: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  "data-cy"?: string;
}) => {
  return (
    <Box sx={{ mb: 0.3, width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <TextField
        data-cy={dataCy}
        value={value}
        type={type}
        onChange={onChange}
        fullWidth
        sx={{
          fontSize: '0.875rem',
          backgroundColor: 'white',
          borderRadius: '5px',
          color: 'black',
          width: '90%'
        }}
        inputProps={{
          style: {
            padding: '5px 10px',
            color: 'black',
            width: '100%'
          },
        }}
      />
    </Box>
  );
};

const ImageFilterRow = ({
  label,
  filter,
  onChange,
  "data-cy": dataCy,
}: {
  label: string;
  filter: ImageFilter;
  onChange: (newValue: ImageFilter) => void;
  "data-cy"?: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: '95%' }}>
    <Checkbox
      data-cy={dataCy}
      checked={filter?.checked || false}
      onChange={(e) => onChange({ ...filter, checked: e.target.checked })}
      size="small"
      sx={{
        mt: 2,
        '&.Mui-checked': { color: (theme) => theme.palette.status.accent },
      }}
    />
    <Select
      value={filter?.operator || "="}
      onChange={(e) => onChange({ ...filter, operator: e.target.value })}
      sx={{
        fontSize: '0.875rem',
        width: 'auto',
        maxWidth: '60px',
        padding: 0,
        mt: 2,
        backgroundColor: 'white',
        borderRadius: '5px',
        color: 'black',
        '& .MuiSelect-select': { padding: '5px 10px' },
        '& .MuiSvgIcon-root': { color: 'black' },
      }}
    >
      {["=", "<", ">", "<=", ">="].map((op) => (
        <MenuItem key={op} value={op}>{op}</MenuItem>
      ))}
    </Select>
    <TextFieldComponent
      label={label}
      type="integer"
      value={filter?.imageCount ?? 0}
      onChange={(e) => onChange({ ...filter, imageCount: Number(e.target.value) })}
    />
  </Box>
);

const DropdownField = ({
  label,
  value,
  onChange,
  options,
  renderValueMap,
  "data-cy": dataCy
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  renderValueMap?: Record<string, string>;
  "data-cy"?: string;
}) => (
  <FormControl fullWidth sx={{ mb: 0.3 }}>
    <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
    <Select
      data-cy={dataCy}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      renderValue={(selected) =>
        renderValueMap ? (renderValueMap[selected] || "Any") : (selected || "All")
      }
      sx={{
        fontSize: '0.875rem',
        backgroundColor: 'white',
        borderRadius: '5px',
        color: 'black',
        width: '90%',
        "& .MuiSelect-select": { padding: '7px 10px' },
        "& .MuiSvgIcon-root": { color: 'black' },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {renderValueMap ? renderValueMap[opt] : opt || "All"}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);


interface FiltersSidebarProps {
  toggleSidebar: () => void;
}

const FiltersSidebar: React.FC<FiltersSidebarProps> = ({ toggleSidebar }) => {
  const { filters: globalAssetFilters, updateFilters } = useAssetsContext();
  
  const [localFilters, setLocalFilters] = useState<AssetFilters>(globalAssetFilters);
  const { selectedSystem} = useSession();

  useEffect(() => {
    setLocalFilters(globalAssetFilters);
  }, [globalAssetFilters]);

  const handleChange = (field: keyof AssetFilters, value: string | boolean | number | null | undefined | ImageFilter) => {
    setLocalFilters((prev: any) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    updateFilters(localFilters);
  };

  const handleSidebarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    applyFilters();
  };

  const clearFilters = () => {
    setLocalFilters(defaultFilters);
    updateFilters(defaultFilters);
  };

  const filtersApplied = hasActiveAssetFilters(localFilters);

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: theme => theme.palette.primary.main,
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
          mb: 1,
          borderBottom: '1px solid #444', // optional styling for separation
        }}
      >
        <Typography variant="h6" sx={{ mr: 2 }}>
          Filters
        </Typography>
        <IconButton data-cy="filters-toggle-btn" onClick={toggleSidebar}>
          <FilterAltOutlinedIcon
            sx={{
              color: (theme) => (filtersApplied ? theme.palette.status.accent : 'inherit')
            }}
          />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          pb: 2,
        }}
        onKeyDown={handleSidebarKeyDown}
      >

        <TextFieldComponent
          label="Asset ID"
          type="text"
          data-cy="filters-asset-id-input"
          value={localFilters.asset_id || ''}
          onChange={(e) => handleChange("asset_id", e.target.value)}
        />

        <Box sx={{ mb: 0.5, width: '90%' }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>
            Date Range (default last 7 days)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              data-cy="filters-start-date-input"
              type="date"
              value={localFilters.start_date || ""}
              onChange={(e) => handleChange("start_date", e.target.value)}
              fullWidth
              sx={{
                backgroundColor: 'white',
                borderRadius: '5px',
                color: 'black',
                width: '90%',
                "& input": {
                  fontSize: '0.85rem',
                  padding: '7px 10px',
                  color: 'black',
                }
              }}
            />
            <TextField
              data-cy="filters-end-date-input"
              type="date"
              value={localFilters.end_date || ""}
              onChange={(e) => handleChange("end_date", e.target.value)}
              fullWidth
              sx={{
                backgroundColor: 'white',
                borderRadius: '5px',
                color: 'black',
                width: '90%',
                "& input": {
                  fontSize: '0.85rem',
                  padding: '7px 10px',
                  color: 'black',
                }
              }}
            />
          </Box>
        </Box>

        <DropdownField
          label="Configuration"
          data-cy="filters-configuration-select"
          value={localFilters.configuration ?? ''}
          onChange={(val) => handleChange("configuration", val)}
          options={["", ...(selectedSystem?.configurations ? Object.keys(selectedSystem.configurations) : [])]}
        />


        <DropdownField
          label="Processing Status"
          data-cy="filters-processing-status-select"
          value={localFilters.processingStatus}
          onChange={(val) => handleChange("processingStatus", val)}
          options={["any", "complete", "not complete"]}
          renderValueMap={{
            any: "Any",
            complete: "Complete",
            "not complete": "Not Complete"
          }}
        />

        <DropdownField
          label="Syndication Status"
          data-cy="filters-syndication-status-select"
          value={localFilters.syndicationStatus}
          onChange={(val) => handleChange("syndicationStatus", val)}
          options={["any", "complete", "failed", "partial success", "not complete"]}
          renderValueMap={{
            any: "Any",
            complete: "Complete",
            failed: "Failed",
            "partial success": "Partial Success",
            "not complete": "Not Complete"
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", mt: 1, mb: 0.3 }}>
          <Checkbox
            data-cy="filters-archived-checkbox"
            checked={localFilters.archived || false}
            onChange={(e) => handleChange("archived", e.target.checked)}
            size="small"
            sx={{ '&.Mui-checked': { color: (theme) => theme.palette.status.accent } }}
          />
          <Typography variant="body2" sx={{ ml: 1 }}>Show Archived</Typography>
        </Box>

        {[
          { label: "Cropped Images", field: "croppedImages" },
          { label: "Background Replaced", field: "bgrImages" },
          { label: "Exterior Images", field: "exteriorImages" },
          { label: "Featured Images", field: "featuredImages" },
          { label: "Manual Images", field: "manualImages" },
          { label: "Panoramic Images", field: "panoramicImages" },
          { label: "Undercarriage Images", field: "ucImages" },
        ].map(({ label, field }) => (
          <ImageFilterRow
            key={field}
            label={label}
            data-cy={`filters-${field}-checkbox`}
            filter={localFilters[field as keyof AssetFilters] as ImageFilter}
            onChange={(newValue) => handleChange(field as keyof AssetFilters, newValue)}
          />
        ))}

        <Box sx={{ display: "flex", width: '100%', alignContent: "end", mt: 1.5, justifyContent: 'space-around' }}>
          <Button data-cy="filters-clear-btn" variant="outlined" color="secondary" onClick={clearFilters}>
            Clear
          </Button>
          <Button data-cy="filters-apply-btn" variant="contained" color="warning" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default FiltersSidebar;
